'use client';

import Link from 'next/link';
import type { ReactNode, MouseEvent } from 'react';
import { MACRO_CELL_HEX } from '@/lib/constants/colors';

// The landing page's CTA look, defined once so its calls-to-action stay in
// sync: a white rounded rectangle framed by the 9 brand cube hues at 0.6
// opacity (the same faded rainbow as <SpectrumBar>, the line under the
// footer), with a soft holistic shadow so the light stripes separate from
// the page. Kept apart from components/ui/Button on purpose so the desktop
// landing stays pixel-identical; hover is a flat tint of the inner pill (no
// scale), matching Button's restrained behavior.
const hexToRgba = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};
export const STRIPE_BORDER = `linear-gradient(90deg, ${MACRO_CELL_HEX
  .map((hex, i) => {
    const c = hexToRgba(hex, 0.6);
    return `${c} ${((i * 100) / MACRO_CELL_HEX.length).toFixed(2)}%, ${c} ${(((i + 1) * 100) / MACRO_CELL_HEX.length).toFixed(2)}%`;
  })
  .join(', ')})`;
export const STRIPE_SHADOW = '0 2px 10px rgba(15, 23, 42, 0.16), 0 0 5px rgba(15, 23, 42, 0.10)';

interface StripeButtonProps {
  href: string;
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  prefetch?: boolean;
  /** Outer wrapper classes — control display/width (e.g. "hidden md:inline-flex"). */
  className?: string;
  /** Inner white-pill classes — e.g. tweak padding or text size. */
  innerClassName?: string;
}

export default function StripeButton({
  href,
  children,
  onClick,
  prefetch,
  className = 'inline-flex',
  innerClassName = '',
}: StripeButtonProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      prefetch={prefetch}
      className={`group p-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 ${className}`}
      style={{ backgroundImage: STRIPE_BORDER, boxShadow: STRIPE_SHADOW }}
    >
      <span
        className={`flex items-center justify-center gap-2 rounded-md bg-white px-7 py-3 text-base font-bold text-text transition-colors group-hover:bg-cta-secondary ${innerClassName}`}
      >
        {children}
      </span>
    </Link>
  );
}
